import React, { useMemo } from 'react';
import Router from 'next/router';
import { gql } from '@soundxyz/gql-string';
import { styled } from '@components-shared/src/stitches';
import { FeedActivityItem } from '@components/Feed/Activities/FeedActivityItem';
import { CommentButtonAction, PostActionButtons } from '@components/Feed/Post/ActionButtons';
import { PurchaseCard } from '@components/SongCards/PurchaseCard';
import {
  GridCarousel,
  SSR_SLIDE_WIDTH_DEFAULT_FEED,
} from '@components/main-components/carousel/GridCarousel';
import { MediaQuery } from '@context/mediaQuery';
import { FeedPostActionsInfoFragmentDoc, type FragmentType } from '@graphql/reactQuery';
import { getFragment, UserCollectedManySongsActivityFragmentDoc } from '@graphql/reactQuery';
import { useWindowSize } from '@hooks/useWindowSize';
import { trackClickEvent } from '@lib/analytics';
import { homePlaylist } from '@store/audio/AudioQueue';
import { getSingularOrPlural } from '@utils/helpers';
import { webappUriPath } from '@utils/url';
import { collectionVerbForActivityTypes } from './helpers';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment UserCollectedManySongsActivity on UserCollectedManySongsAggregate {
    numReleases
    collectedReleases {
      release {
        id
        ...PurchaseCard
        socialProofCollectors(userId: $userId) {
          ...PurchaseCardCollectedByUser
        }
      }
      ...NFTSaleInfo
    }
    user {
      ...UserActivityDetail
    }
    collectionTypes
    post {
      ...FeedPostActionsInfo
    }
  }
`);

export const UserCollectedManySongs = ({
  activityFrag,
  postActionsFrag,
  timeStamp,
}: {
  activityFrag: FragmentType<UserCollectedManySongsActivityFragmentDoc>;
  postActionsFrag?: FragmentType<FeedPostActionsInfoFragmentDoc> | null;
  timeStamp: number;
}) => {
  const { tablet } = MediaQuery.useContainer();
  const activity = getFragment(UserCollectedManySongsActivityFragmentDoc, activityFrag);
  const { collectedReleases, user, numReleases, collectionTypes } = activity;
  const collectionActivityVerb = collectionVerbForActivityTypes(new Set(collectionTypes));

  const releaseSlides = useMemo(
    () =>
      collectedReleases.map(collectedRelease => {
        const release = collectedRelease.release;

        return {
          id: release.id,
          slide: (
            <PurchaseCard
              key={release.id}
              releaseFrag={release}
              playlist={homePlaylist}
              collectedByFrag={collectedRelease.release.socialProofCollectors}
              saleInfoFrag={collectedRelease}
            />
          ),
        };
      }),
    [collectedReleases],
  );

  const subtext = `${collectionActivityVerb} ${
    numReleases > 1 ? numReleases : 'a'
  } ${getSingularOrPlural({
    word: 'song',
    count: numReleases,
  })}`;

  const { width: windowWidth } = useWindowSize();

  const columnAmount = useMemo(() => {
    if (windowWidth) {
      if (windowWidth >= 1280) return 3;
      if (windowWidth >= 768) return 3;
      if (windowWidth >= 640) return 2.5;
    } else {
      return 3;
    }
    return 1.5;
  }, [windowWidth]);

  const post = getFragment(FeedPostActionsInfoFragmentDoc, postActionsFrag);

  const content = (
    <FeedActivityItem
      dropdownContent={null}
      userFrag={user}
      description={subtext}
      timestamp={timeStamp}
    >
      {!tablet && <Mask />}
      <GridCarousel
        slides={releaseSlides}
        onEndReached={null}
        onInternalActiveIndexChange={null}
        columnAmount={columnAmount}
        hideArrows={false}
        externalActiveIndex={null}
        spacing={null}
        alignSlidesLeft={false}
        ssrSlideWidth={SSR_SLIDE_WIDTH_DEFAULT_FEED}
        columnType="large"
      />
      {postActionsFrag && (
        <div className="my-3">
          <PostActionButtons
            commentButtonAction={CommentButtonAction.ShowModal}
            postFrag={postActionsFrag}
            sourceLocation="Collector Activity Card"
            resourceOwnerTwitterHandle={null}
          />
        </div>
      )}
    </FeedActivityItem>
  );

  return !!post ? (
    <div
      className="-m-3 cursor-pointer rounded-lg p-3 hover:bg-neutral50 hover:[&_.read-more-button]:bg-neutral200 [&_.read-more-overlay]:hover:from-neutral50"
      data-feed-clickable-div
      onClick={e => {
        if (e.target instanceof HTMLElement) {
          if (e.target.hasAttribute('data-feed-clickable-div')) {
            Router.push(webappUriPath(post.webappUri));

            trackClickEvent({
              event: 'Post Link Clicked',
              properties: {
                associationId: post.id,
                associationType: 'Post',
                sourceComponent: 'Collector Activity Feed',
              },
            });
          }
        }
      }}
    >
      {content}
    </div>
  ) : (
    content
  );
};

export const ComponentWrapper = styled('div', {
  position: 'relative',
  marginLeft: 0,

  '@tabletLandscape': {
    marginLeft: 65,
  },
});

export const Mask = styled('div', {
  opacity: 0.5,
  maskImage: 'url(/star.png)',
  maskSize: '34px',
  background:
    'radial-gradient(80% 100% at 5% 10%, rgba(255, 255, 255, 0) 0%, #FFFFFF 100%), rgba(0,0,0, 0.5)',
  transform: 'rotate(180deg) matrix(-1, 0, 0, 1, 0, 0)',
  width: '50%',
  height: 138,
  left: '50%',
  position: 'absolute',
  bottom: 0,
  marginLeft: 12,
});

export const CardWrapper = styled('div', {
  display: 'flex',
  background: '$white',
});
